import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuthStore } from "@/stores/auth";

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          "error-callback"?: () => void;
          "expired-callback"?: () => void;
          "timeout-callback"?: () => void;
          "unsupported-callback"?: () => void;
          theme?: "light" | "dark" | "auto";
        },
      ) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

export function RegisterPage() {
  const register = useAuthStore((s) => s.register);
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get("returnTo");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const submittingRef = useRef(false);

  const resetCaptcha = useCallback(() => {
    setCaptchaToken("");
    if (widgetIdRef.current !== null && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  }, []);

  const renderTurnstile = useCallback(() => {
    if (!TURNSTILE_SITE_KEY || !window.turnstile || !turnstileRef.current) return;
    if (widgetIdRef.current) return; // Already rendered

    widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
      sitekey: TURNSTILE_SITE_KEY,
      callback: (token: string) => {
        setCaptchaToken(token);
        setError("");
      },
      "error-callback": () => setCaptchaToken(""),
      "expired-callback": () => setCaptchaToken(""),
      "timeout-callback": () => setCaptchaToken(""),
      "unsupported-callback": () => setCaptchaToken(""),
      theme: "dark",
    });
  }, []);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;

    // If Turnstile script already loaded
    if (window.turnstile) {
      renderTurnstile();
      return;
    }

    // Load the Turnstile script
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.onload = () => renderTurnstile();
    document.head.appendChild(script);

    return () => {
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [renderTurnstile]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;
    setError("");

    if (TURNSTILE_SITE_KEY && !captchaToken) {
      setError(
        "Please complete the security check. If it keeps failing, wait a minute and try again.",
      );
      return;
    }

    submittingRef.current = true;
    setLoading(true);
    try {
      await register(email, password, name || undefined, captchaToken || undefined);
      if (returnTo) localStorage.setItem("returnTo", returnTo);
      setRegistered(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Registration failed");
      resetCaptcha();
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  if (registered) {
    return (
      <div className="space-y-5 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-stage-stone/20">
          <svg className="h-7 w-7 text-stage-stone" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-ink">Check your email</h2>
        <p className="text-sm text-ink-3">
          We sent a verification link to <span className="font-medium text-ink">{email}</span>.
          Click the link to activate your account.
        </p>
        <p className="text-sm text-ink-3">
          Didn't receive it? Check your spam folder or{" "}
          <Link
            to={returnTo ? `/login?returnTo=${encodeURIComponent(returnTo)}` : "/login"}
            className="text-ink-2 transition-colors hover:text-ink"
          >
            try signing in
          </Link>{" "}
          to resend.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="space-y-2">
        <label className="text-sm font-medium text-ink-2">Name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="input"
          placeholder="Optional"
        />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium text-ink-2">Email</label>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="input"
          placeholder="you@example.com"
        />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium text-ink-2">Password</label>
        <input
          type="password"
          required
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="input"
          placeholder="Min 8 characters"
        />
      </div>
      {TURNSTILE_SITE_KEY && (
        <div ref={turnstileRef} className="flex justify-center" />
      )}
      {error && (
        <p className="text-sm text-bad">{error}</p>
      )}
      <button
        type="submit"
        disabled={loading}
        className="btn btn--accent w-full"
      >
        {loading ? "Creating account..." : "Create account"}
      </button>
      <p className="text-center text-sm text-ink-3">
        Already have an account?{" "}
        <Link
          to={returnTo ? `/login?returnTo=${encodeURIComponent(returnTo)}` : "/login"}
          className="text-ink-2 transition-colors hover:text-ink"
        >
          Sign in
        </Link>
      </p>
    </form>
  );
}
